import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar,
  FileText,
  Users,
  TrendingUp,
  Clock,
  BookOpen,
  Tag,
  ArrowLeft
} from 'lucide-react'
import { supabase, BlogPost } from '@/lib/supabase'
import { BlogPostEditor } from '@/components/blog/BlogPostEditor'
import { ContentPreview } from '@/components/ui/content-preview'

// Database operations
const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

const deleteBlogPost = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

export default function BlogManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [currentView, setCurrentView] = useState<'list' | 'editor' | 'preview'>('list')
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null)
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const queryClient = useQueryClient();

  // Fetch blog posts from database
  const { data: posts = [], isLoading, error } = useQuery({
    queryKey: ['blog_posts'],
    queryFn: fetchBlogPosts
  });

  // Delete blog post mutation
  const deleteMutation = useMutation({
    mutationFn: deleteBlogPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
    }
  });

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.content.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status: BlogPost['status']) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'archived': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  }

  const handleNewPost = () => {
    setSelectedPost(null);
    setCurrentView('editor');
  }

  const handleEditPost = (post: BlogPost) => {
    setSelectedPost(post);
    setCurrentView('editor');
  }

  const handleViewPost = (post: BlogPost) => {
    setPreviewPost(post);
    setIsPreviewOpen(true);
  }

  const handleEditorSave = (post: BlogPost) => {
    setCurrentView('list');
    setSelectedPost(null);
  }

  const handleEditorCancel = () => {
    setCurrentView('list');
    setSelectedPost(null);
  }

  const handleEditorPreview = (post: BlogPost) => {
    setPreviewPost(post);
    setIsPreviewOpen(true);
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Blog Posts</h2>
          <p className="text-gray-600">Unable to load blog posts data. Please try again later.</p>
        </div>
      </div>
    );
  }

  const stats = {
    total: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    draft: posts.filter(p => p.status === 'draft').length,
    archived: posts.filter(p => p.status === 'archived').length
  }

  // Mock categories and tags for display
  const categories = [
    { id: 'cat-1', name: 'Masonry Tips', count: 5 },
    { id: 'cat-2', name: 'Home Maintenance', count: 8 },
    { id: 'cat-3', name: 'Project Showcase', count: 12 },
    { id: 'cat-4', name: 'Industry News', count: 3 }
  ]

  const popularTags = [
    { id: 'tag-1', name: 'brick repair', count: 15 },
    { id: 'tag-2', name: 'tuckpointing', count: 12 },
    { id: 'tag-3', name: 'chimney', count: 8 },
    { id: 'tag-4', name: 'maintenance', count: 10 }
  ]

  // Show editor view
  if (currentView === 'editor') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleEditorCancel}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Posts
          </Button>
        </div>
        <BlogPostEditor
          post={selectedPost || undefined}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onPreview={handleEditorPreview}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Blog Management</h1>
          <p className="text-muted-foreground">
            Create and manage blog content to educate customers and improve SEO.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Calendar className="mr-2 h-4 w-4" />
            Schedule Post
          </Button>
          <Button onClick={handleNewPost}>
            <Plus className="mr-2 h-4 w-4" />
            New Post
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All blog content
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.published}</div>
            <p className="text-xs text-muted-foreground">
              Live on website
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Drafts</CardTitle>
            <Edit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft}</div>
            <p className="text-xs text-muted-foreground">
              Work in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Archived</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.archived}</div>
            <p className="text-xs text-muted-foreground">
              Archived posts
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle>Blog Posts</CardTitle>
              <CardDescription>
                Manage your blog content and publishing schedule.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search posts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    More Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Posts Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Post</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead>Read Time</TableHead>
                    <TableHead>SEO Score</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPosts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{post.title}</div>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            /{post.slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(post.status)}>
                          {post.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">Admin User</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {post.published_at 
                            ? new Date(post.published_at).toLocaleDateString()
                            : '—'
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {post.content ? `${Math.ceil(post.content.split(' ').length / 200)} min` : '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500" 
                              style={{ width: '92%' }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground">92%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewPost(post)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditPost(post)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(post.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Categories */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {categories.map((category) => (
                <div key={category.id} className="flex items-center justify-between">
                  <span className="text-sm">{category.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {category.count}
                  </Badge>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="w-full mt-2">
                <Plus className="mr-2 h-4 w-4" />
                Add Category
              </Button>
            </CardContent>
          </Card>

          {/* Popular Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Popular Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {popularTags.map((tag) => (
                  <Badge key={tag.id} variant="outline" className="text-xs">
                    {tag.name} ({tag.count})
                  </Badge>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="w-full mt-3">
                <Tag className="mr-2 h-4 w-4" />
                Manage Tags
              </Button>
            </CardContent>
          </Card>

          {/* Publishing Calendar */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Publishing Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm">
                  <div className="font-medium">This Week</div>
                  <div className="text-muted-foreground">2 posts scheduled</div>
                </div>
                <div className="text-sm">
                  <div className="font-medium">Next Week</div>
                  <div className="text-muted-foreground">1 post scheduled</div>
                </div>
                <div className="text-sm">
                  <div className="font-medium">This Month</div>
                  <div className="text-muted-foreground">8 posts total</div>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-3">
                <Calendar className="mr-2 h-4 w-4" />
                View Calendar
              </Button>
            </CardContent>
          </Card>

          {/* SEO Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Average Score</span>
                  <span className="font-medium">87%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Top Performing</span>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Need Improvement</span>
                  <span className="text-sm text-muted-foreground">3 posts</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {previewPost?.title || 'Blog Post Preview'}
            </DialogTitle>
          </DialogHeader>
          {previewPost && (
            <div className="space-y-6">
              {/* Post Meta */}
              <div className="border-b pb-4">
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span>By {previewPost.author || 'Admin User'}</span>
                  <span>•</span>
                  <span>{previewPost.published_at ? new Date(previewPost.published_at).toLocaleDateString() : 'Unpublished'}</span>
                  <span>•</span>
                  <span>{previewPost.reading_time || Math.ceil(previewPost.content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200)} min read</span>
                  <span>•</span>
                  <Badge className={getStatusColor(previewPost.status)}>
                    {previewPost.status}
                  </Badge>
                </div>
                {previewPost.excerpt && (
                  <p className="mt-3 text-lg text-muted-foreground italic">
                    {previewPost.excerpt}
                  </p>
                )}
                {previewPost.featured_image && (
                  <div className="mt-4 aspect-video rounded-lg overflow-hidden">
                    <img 
                      src={previewPost.featured_image} 
                      alt={previewPost.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              {/* Post Content */}
              <ContentPreview 
                content={previewPost.content}
                showBorder={false}
                maxHeight="none"
                className="prose-lg"
              />

              {/* Tags */}
              {previewPost.tags && previewPost.tags.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Tags:</h4>
                  <div className="flex flex-wrap gap-2">
                    {previewPost.tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}